/**
 *    VALIDATION
 *    app-wide validation properties and methods
 */

// app
import utilSvc from './utilSvc';

// types
import { HttpError422, HttpImportResult } from '../../global';
import { ValidPropText } from './components/validation/ValidInputText';
import { ValidPropRadioBool } from './components/validation/ValidInputRadioBool';
import { ValidPropRadioText } from './components/validation/ValidInputRadioText';
import { ValidPropDroplistText } from './components/validation/ValidInputDroplistText';
import { TblFltOption } from './table/table.types';

const validSvc = {
    isNull: isNull,
    isText: isText,
    isNumber: isNumber,
    isInteger: isInteger,
    isIntegerRange,
    isJson: isJson,
    isDate: isDate,
    isCE: isCE,

    newProp: newProp,
    newPropText: newPropText,
    newPropRadioBool: newPropRadioBool,
    newPropRadioText: newPropRadioText,
    newPropDroplistText: newPropDroplistText,
    newPropStatusText: newPropStatusText,

    testPropText: testPropText,
    testDroplistText: testDroplistText,
    testRadioText: testRadioText,

    httpGetEntityResp: httpGetEntityResp,
    httpGetListResp: httpGetListResp,
    httpUpsert200Resp: httpUpsert200Resp,
    httpUpsertErrResp: httpUpsertErrResp,
    http422ToModel: http422ToModel,
    http422ToString: http422ToString,
    http422ToModelType: http422ToModelType,
    http422ImportResultToString: http422ImportResultToString,

    blobSupported,
};

/*
 var service = {
		isDate: isDate,
		isNumber: isNumber,
		isInteger: isInteger,
		isJson: isJson,

		isCE: isCE,
		isRX: isRX,
		isVEN: isVEN,
		isEntity: isEntity,
		isTPA: isTPA,

		newProp: newProp,
		newPropDate: newPropDate,
		newPropDOBDate: newPropDOBDate,
		newPropTodayDate: newPropTodayDate,
		newPropDateExact: newPropDateExact,
		newPropDateNull: newPropDateNull,
		propDateValid: propDateValid,
		datePropRange: datePropRange,
		setResult: setResult,

		httpGetListResp: httpGetListResp,
		httpGetOneResp: httpGetOneResp,
		httpGetEntityResp: httpGetEntityResp,
		httpUpsert200Resp: httpUpsert200Resp,
		httpBulkUpsert200Resp: httpBulkUpsert200Resp,
		httpUpsertErrResp: httpUpsertErrResp,

		authEntity: authEntity,
		authVM: authVM,

		pageSize: pageSize,
		sortByApi: sortByApi,
		sortByUi: sortByUi,

		blobSupported: blobSupported,
		uploadSupported: uploadSupported

	};

	return service;
*/

/**
 *    NULL CHECK
 *    includes undefined and null, but not falsy values like false or -1
 */

function isNull(val: any): boolean {
    return typeof val == 'undefined' || val === null;
}

/**
 *    PROPERTY
 *    controllers and repos communicate validation erros using validation properties
 */

export type ValidErr = Error | string;
export enum ValidStatusType {
    ONBOARDING = 'ONBOARDING',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    SUSPENDED = 'SUSPENDED',
    DELETED = 'DELETED',
}

export type ValidOption = {
    lbl: string;
    val: string;
};
export type ValidStatusOption = {
    lbl?: string;
    val?: string;
    type?: ValidStatusType;
    cssName?: string;
};

export type ValidProp = {
    key: string;
    lbl: string;
    val: any;
    req: boolean;
    err: ValidErr | undefined;
};
export enum ValidPropType {
    None = 'None',
    Text = 'Text',
    RadioBool = 'RadioBool',
    RadioText = 'RadioText',
    DroplistText = 'DroplistText',
    StatusText = 'StatusText',
}
/** @deprecated */
export type ValidPropStatusText = {
    type: ValidPropType.StatusText;
    key: string;
    lbl: string;
    val: string;
    def: string;
    options: ValidStatusOption[];
    err: ValidErr | undefined;
};

function newProp(key: string, lbl?: string, val?: any, req?: boolean): ValidProp {
    return {
        key: key,
        lbl: lbl || '',
        val: val,
        req: !!req,
        err: undefined,
    };
}

function newPropText(key: string, lbl?: string, val?: string, def?: string): ValidPropText {
    return {
        key: key,
        lbl: lbl || '',
        val: !!val ? val : !!def ? def : '',
        err: '',
    };
}

function testPropText(prop: ValidPropText, req?: boolean, min?: number, max?: number): boolean {
    prop.err = '';

    const val = typeof prop.val === 'string' ? prop.val.trim() : '';
    if (!!req && val.length < 1) {
        prop.err = 'required';
    } else if (typeof min !== 'undefined' && val.length > 0 && val.length < min) {
        prop.err = 'min chars ' + min.toString();
    } else if (typeof max !== 'undefined' && val.length > 0 && val.length > max) {
        prop.err = 'max chars ' + max.toString();
    }

    return !!!prop.err;
}

function defaultBoolOptions(): TblFltOption[] {
    return [
        { lbl: 'No', val: 'false' },
        { lbl: 'Yes', val: 'true' },
    ];
}

function newPropRadioBool(
    key: string,
    lbl?: string,
    val?: boolean,
    def?: boolean,
    options?: ValidOption[],
): ValidPropRadioBool {
    return {
        key: key,
        lbl: lbl || '',
        val: typeof val === 'undefined' && typeof def !== 'undefined' ? def : val,
        def: def,
        options: Array.isArray(options) ? options : defaultBoolOptions(),
        err: '',
    };
}

function newPropRadioText(
    key: string,
    lbl?: string,
    val?: any,
    def?: string,
    options?: ValidOption[],
): ValidPropRadioText {
    return {
        key: key,
        lbl: lbl || '',
        val: !!val ? val : !!def ? def : '',
        def: def || '',
        options: Array.isArray(options) ? options : [],
        err: '',
    };
}

function testRadioText(prop: ValidPropRadioText, req?: boolean): boolean {
    prop.err = '';
    if (!!req && !validSvc.isText(prop.val)) {
        prop.err = 'required';
    }
    return !prop.err;
}

function newPropDroplistText(
    key: string,
    options?: ValidOption[],
    lbl?: string,
    val?: string,
    def?: string,
): ValidPropDroplistText {
    return {
        key: key,
        lbl: lbl || '',
        val: !!val ? val : !!def ? def : '',
        def: def || '',
        options: Array.isArray(options) ? options : [],
        err: '',
    };
}

function testDroplistText(prop: ValidPropDroplistText, req?: boolean): boolean {
    prop.err = '';
    if (!!req && !validSvc.isText(prop.val)) {
        prop.err = 'required';
    }
    return !prop.err;
}

function newPropStatusText(
    key: string,
    options?: ValidStatusOption[],
    lbl?: string,
    val?: string,
    def?: string,
): ValidPropStatusText {
    const opts = [] as ValidStatusOption[];
    let optVal = '';
    if (Array.isArray(options)) {
        options.forEach(opt => {
            if (!opt.type && !opt.val) {
                throw new Error('validSvc.newPropStatusText, Status option requires type or val');
            }
            optVal = !!opt.val ? opt.val : !!opt.type ? opt.type : '';
            opts.push({
                type: !!opt.type ? opt.type : (optVal as ValidStatusType),
                val: optVal,
                lbl: !!lbl ? lbl : optVal,
            });
        });
    }
    return {
        type: ValidPropType.StatusText,
        key: key,
        lbl: lbl || '',
        val: !!val ? val : !!def ? def : '',
        def: def || '',
        options: opts,
        err: undefined,
    };
}

// }

// function newPropDateExact(val, min, max, fail, msg) {

// 	// incoming date val is in "exact" text format of mm/dd/yyyy
// 	// don't modify the incoming value, its already been parsed by a UTC function earlier

// 	var dt = newPropDate(val, min, max, fail, msg);

// 	// replace value with exact text passed in
// 	dt.val = val;

// 	return dt;

// }

// function newPropDate(val, min, max, fail, msg) {

// 	// date validation property always has min and max values
// 	// note: default values are today with no time

// 	var dtToday = new Date();

// 	// init with valid date type
// 	var dt = {
// 		val: dtToday,
// 		min: dtToday,
// 		max: dtToday
// 	};

// 	// assign
// 	dt.val = isDate(val) ? val : utilSvc.dt.zeroTime(dtToday);
// 	dt.min = isDate(min) ? min : utilSvc.dt.zeroTime(appConst.app.liveDate);
// 	dt.max = isDate(max) ? max : utilSvc.dt.zeroTime(dtToday);
// 	dt.fail = typeof(fail) === 'boolean' ? fail : false;
// 	dt.msg = isText(msg) ? msg : '';

// 	return dt;

// }

// function newPropDOBDate(val) {
// 	var dtToday = utilSvc.dt.zeroTime(new Date());
// 	var dtMin = utilSvc.dt.zeroTime(utilSvc.dt.addDays(new Date(), (-43800))); // 120 years days back from today
// 	return newPropDate(val, dtMin, dtToday);
// }

// function newPropTodayDate() {

// 	var dtToday = utilSvc.dt.zeroTime(new Date());
// 	var dtMin = utilSvc.dt.zeroTime(utilSvc.dt.addDays(new Date(), (-3650))); // 10 years days back from today
// 	return newPropDate(dtToday, dtMin, dtToday);
// }

// function newPropDateNull(min, max, fail, msg) {

// 	// allows empty dates for non-required fields (instead of newPropDate which will put in today's date)
// 	// note: default values are today with no time

// 	var dt = {
// 		val: null
// 	};
// 	dt.min = isDate(min) ? min : utilSvc.dt.zeroTime(appConst.app.liveDate);
// 	dt.max = isDate(max) ? max : utilSvc.dt.zeroTime(new Date());
// 	dt.fail = typeof(fail) === 'boolean' ? fail : false;
// 	dt.msg = isText(msg) ? msg : '';

// 	return dt;

// }

/**
 *    STRING
 */

function isText(testVal: any): boolean {
    return utilSvc.str.isText(testVal);
}

function isJson(txt: string): boolean {
    // parse string for Json without throwing an error
    try {
        const test = JSON.parse(txt);
        if (!!test) {
            return true;
        }
    } catch (err) {
        // do nothing
    }

    return false;
}

/**
 *    NUMBER
 */

function isNumber(val: any): boolean {
    return !isNaN(val) && val !== null && val !== '';
}

function isInteger(val: any): boolean {
    return isNumber(val) && parseInt(val) === parseFloat(val);
}

function isIntegerRange(val: any, min?: number, max?: number): boolean {
    if (!isNumber(val)) return false;
    if (min === undefined && max !== undefined) {
        return val <= max;
    }
    if (max === undefined && min !== undefined) {
        return val >= min;
    } else if (min !== undefined && max !== undefined) {
        return val >= min && val <= max;
    }

    return false;
}

/**
 *    ENTTY VALUE CHECK
 *    CE OR RX OR VEN
 */

function isEntity(entity: any, noneOk: any, allOk: any): boolean {
    // has no value
    var checkVal = utilSvc.str.toText(entity);
    if (!isText(checkVal)) {
        return false;
    }
    checkVal = checkVal.toUpperCase();

    // is NONE
    if (checkVal == 'NONE' && !!!noneOk) {
        return false;
    }

    // is ALL
    if (checkVal == 'ALL' && !!!allOk) {
        return false;
    }

    // pass
    return true;
}

/**
 *    DATE
 */

function isDate(dt: any): boolean {
    return utilSvc.dt.isValid(dt);
}

function isCE(ce, noneOk, allOk): boolean {
    return isEntity(ce, noneOk, allOk);
}

// 	function propDateCheck(dt, regex) {

// 		// check date properties, return name of property which failed validation, or null

// 		// invalid object
// 		if(!!!dt) {
// 			return 'obj';
// 		}

// 		// required
// 		if(!utilSvc.str.isText(utilSvc.str.toText(dt.val))) {
// 			return 'req';
// 		}

// 		// regex
// 		if(!!regex && !regex.test(dt.val)) {
// 			return 'regex';
// 		}

// 		// val
// 		var val = utilSvc.dt.parse(dt.val);
// 		if(val === null) {
// 			return 'val';
// 		}

// 		// min
// 		var dtMin = utilSvc.dt.parse(dt.min);
// 		if(dtMin !== null && utilSvc.dt.diffMilli(dtMin, val) < 0) {
// 			return 'min';
// 		}

// 		// max
// 		var dtMax = utilSvc.dt.parse(dt.max);
// 		if(dtMax !== null && utilSvc.dt.diffMilli(dtMax, val) > 0) {
// 			return 'max';
// 		}

// 		// pass
// 		return null;

// 	}

// 	function propDateValid(dt, regex) {

// 		// validate date object, add standard fail msg text

// 		var testDt = {
// 			val: 'invalid',
// 			min: 'invalid',
// 			max: 'invalid',
// 			msg: '',
// 			fail: true
// 		};

// 		switch(propDateCheck(dt, regex)) {

// 			case 'obj':
// 				dt = newPropDateNull();
// 				dt.fail = true;
// 				dt.msg = 'invalid object';
// 				return dt;

// 			case 'req':
// 				dt.fail = true;
// 				dt.msg = 'is required';
// 				return dt;

// 			case 'regex':
// 				dt.fail = true;
// 				dt.msg = 'format is m/d/yyyy';
// 				return dt;

// 			case 'val':
// 				dt.fail = true;
// 				dt.msg = 'invalid date';
// 				return dt;

// 			case 'min':
// 				dt.fail = true;
// 				dt.msg = 'must be on or after: ' + $filter('date')(dt.min, 'M/d/yyyy');
// 				return dt;

// 			case 'max':
// 				dt.fail = true;
// 				dt.msg = 'must be on or before: ' + $filter('date')(dt.max, 'M/d/yyyy');
// 				return dt;

// 			case null:
// 				dt.fail = false;
// 				dt.msg = '';
// 				return dt;

// 			default:
// 				throw new Error('unknown date check value: ' + propDateCheck(dt));

// 		}

// 	}

// 	function datePropRange(start, end, minDayRange, maxDayRange) {

// 		// validate a date range between two date props

// 		// invalid range params
// 		if (minDayRange < 0 || maxDayRange < 0 || minDayRange > maxDayRange) {
// 			throw 'validSvc.dateRange, day range params must be > 0, min range must be less than max range';
// 		}

// 		// invalid date prop objects
// 		start = dateProp(start);
// 		end = dateProp(end);
// 		if(start.fail || end.fail) {
// 			return false;
// 		}

// 		// start prop after end prop
// 		if(utilSvc.dt.diffMilli(start.val, end.val) < 0) {
// 			start.fail = true;
// 			start.msg = 'must be earlier or the same as end date';
// 			end.fail = true;
// 			end.msg = 'must be after or the same as start date';
// 			return false;
// 		}

// 		var dayRange = utilSvc.dt.diffDays(start.val, end.val);

// 		// day range < min range
// 		if(dayRange < minDayRange) {
// 			start.fail = true;
// 			start.msg = 'date range must be at least' + minDayRange.toString() + ' days';
// 			end.fail = true;
// 			end.msg = 'date range must be at least' + minDayRange.toString() + ' days';
// 			return false;
// 		}

// 		// day range > max range
// 		if(dayRange > maxDayRange) {
// 			start.fail = true;
// 			start.msg = 'date range must be less than ' + maxRangeDays.toString() + ' days';
// 			end.fail = true;
// 			end.msg = 'date range must be less than ' + maxRangeDays.toString() + ' days';
// 			return false;
// 		}

// 		// pass
// 		return true;

// 	}

// 	function setResult(isValid, prop, msgFail, msgPass) {

// 		// short cut to set property fail result based on a validity test

// 		if(!!isValid) {
// 			prop.fail = 'no';
// 			prop.msg = isText(msgPass) ? msgPass : '';
// 		}
// 		else {
// 			prop.fail = 'yes';
// 			prop.msg = msgFail;
// 		}

// 		return prop;

// 	}

/**
 *    HTTP RESPONSE
 */

function httpData(r: any): boolean {
    // has data
    return !!r && !!r.data;
}

function httpStatus(r: any): boolean {
    // has status
    return !!r && !!r.status && !isNaN(r.status) && r.status >= 0;
}

// function httpStatus200(r: any): boolean {
//     // has status 200
//     return httpStatus(r) && r.status == 200;
// }

function httpEntity(r: any): boolean {
    // has data.Entity{}
    return httpData(r) && !!r.data.Entity;
}

function httpEntityArray(r: any): boolean {
    // has data.Entity[]
    return httpData(r) && Array.isArray(r.data.Entity);
}

function httpGetPaging(r: any): boolean {
    // has data.Paging{}
    return httpData(r) && !!r.data.Paging && !isNaN(r.data.Paging.Size) && r.data.Paging.Size >= 0;
}

function httpGetListResp(r: any): boolean {
    // get list has: status, entity and paging
    return httpStatus(r) && httpEntityArray(r) && httpGetPaging(r);
}

function httpGetEntityResp(r: any): boolean {
    // get detail (one item) has status, paging, array of length 1
    return httpStatus(r) && httpEntity(r);
}

function httpUpsert200Resp(r: any): boolean {
    // create/update upsert has data.Entity.Id
    return !!r && !!r.Entity && isText(r.Entity.Id);
}

function httpUpsertErrResp(r: any): boolean {
    // create/update error has status (409 or 422), data.Entity.ROOT[] and data.Entity.Fields[]
    if (httpStatus(r)) {
        return (
            r.status == 409 ||
            (r.status == 422 &&
                httpEntity(r) &&
                Array.isArray(r.data.Entity.ROOT) &&
                Array.isArray(r.data.Entity.Fields))
        );
    }
    return false;
}

function http422RootToString(err: HttpError422): string {
    if (!err || !err.data || !err.data.Entity || !Array.isArray(err.data.Entity.ROOT)) {
        return '';
    }
    return err.data.Entity.ROOT.join(', ');
}

function http422Parse(err: HttpError422, m?: object): string {
    if (!err || !err.data || !err.data.Entity) {
        return '';
    }

    const result: string[] = [];

    // ROOT added to result
    const rootErr = http422RootToString(err);
    if (rootErr) {
        result.push(rootErr);
    }

    // no fields
    const fields = err.data.Entity.Fields;
    if (!Array.isArray(fields) || fields.length < 1) {
        return result.join(', ');
    }

    if (m) {
        // update model
        fields.forEach(f => {
            if (m[f.Name]) {
                m[f.Name].err = new Error(f.Rule);
            } else {
                // field not found, add to string result
                result.push('422 Field not found on model: ' + f.Name + ' with Rule: ' + f.Rule);
            }
        });
    } else {
        // no model, stringify fields
        result.push(' With Fields: ');
        fields.forEach(f => {
            result.push(f.Name + ': ' + f.Rule);
        });
    }

    return result.join(', ');
}

function http422ToModel(err: HttpError422, m?: object): string {
    return http422Parse(err, m);
}

function http422ToString(err: HttpError422): string {
    return http422Parse(err);
}

// we need a direct to model 422 handler with generic model output type
function http422ToModelType<T>(err: HttpError422, m: T): T {
    if (!err || !err.data || !err.data.Entity) {
        return m;
    }

    // no fields
    const fields = err.data.Entity.Fields;
    if (!Array.isArray(fields) || fields.length < 1) {
        return m;
    }

    // update model
    if (!!m) {
        fields.forEach(f => {
            if (m.hasOwnProperty(f.Name)) {
                m[f.Name].err = !!f.Rule ? f.Rule.toString() : 'unknown'; // MUST be a string
            }
        });
    }

    return m;
}

function http422ImportResultToString(err: any): string {
    // tbd... this eventually needs to be its own UI list of line errors
    try {
        const importResult = err as HttpImportResult;
        let errMsg =
            'Received: ' +
            importResult.Received +
            ', Processed: ' +
            importResult.Processed +
            ', Errors: ' +
            importResult.LineErrors.length +
            '.';
        if (importResult.LineErrors.length > 0) {
            errMsg += ' Line Errors: ';
            // map property Line + Error into one string
            const lineErrs = importResult.LineErrors.map(l => 'Line ' + l.Line.toString() + ': ' + l.Error);
            errMsg += lineErrs.join('\n') + '.';
        }
        return errMsg;
    } catch (e: any) {
        return !!e && validSvc.isText(e.message) ? e.message : 'Unknown import error';
    }
}

// 	function httpGetOneResp(r) {
// 		// get detail (one item) has status, paging, array of length 1
// 		return httpStatus(r) && ((httpEntityArray(r) && r.data.Entity.length == 1) || httpEntity(r));
// 	}

// 	function httpBulkUpsert200Resp(r) {
// 		// create/update upsert has status, data.Entity.Id
// 		return httpStatus(r) && httpEntity(r);
// 	}

// 	/**
// 	 *    AUTH VM
// 	 *    view model structure and values are valid
// 	 */

// 	function authVM(auth) {

// 		if(!!!auth || !!!auth.CE || !isText(auth.CE.val) || !!!auth.RX || !isText(auth.RX.val)) {
// 			return false;
// 		}

// 		return true;

// 	}

// 	/**
// 	 *    AUTH ENTITY
// 	 *    CE, Pharmacy, Vendor Ids all use the same format
// 	 *    note: two key words for state are: 'NONE' and 'ALL'
// 	 */

// 	function authEntity(ent, noneOk, allOk) {

// 		// reset
// 		ent.fail = false;
// 		ent.msg = '';

// 		// no valid characters (even empty should be 'NONE')
// 		if(!isText(ent.val)) {
// 			ent.fail = true;
// 			ent.msg = 'invalid string';
// 		}
// 		else if(ent.val.toUpperCase() == 'NONE' && !noneOk) {
// 			// none not allowed
// 			ent.fail = true;
// 			ent.msg = 'is required';
// 		}
// 		else if(ent.val.toUpperCase() == 'ALL' && !allOk) {
// 			// all not allowed
// 			ent.fail = true;
// 			ent.msg = 'cannot be all';
// 		}

// 		// return
// 		return ent;

// 	}

// 	/**
// 	 *    SORT BY
// 	 */

// 	function sortByApi(sortVM) {

// 		if(!!sortVM && isText(sortVM.Field) && isText(sortVM.Order)) {
// 			if(sortVM.Order == 'ASC' || sortVM.Order == 'DSC') {
// 				return true;
// 			}
// 		}

// 		return false;
// 	}

// 	function sortByUi(sortTxt) {

// 		// use utilSvc, convert to API style, check values
// 		var apiConvert = utilSvc.sorting.sortByApiStyle(sortTxt);
// 		if(!!apiConvert && isText(apiConvert.Field) && (apiConvert.Order == 'ASC' || apiConvert.Order == 'DSC')) {
// 			return true;
// 		}
// 		else {
// 			return false;
// 		}

// 	}

// 	/**
// 	 *    PAGE SIZE
// 	 */

// 	function pageSize(val) {

// 		if(isNaN(val) || val < 1 || val > appConst.app.query.pagingSize) {
// 			return false;
// 		}

// 		return true;
// 	}

// 	/**
// 	 *    ENTTY VALUE CHECK
// 	 *    CE OR RX OR VEN
// 	 */

// 	function isEntity(entity, noneOk, allOk) {

// 		// has no value
// 		var checkVal = utilSvc.str.toText(entity);
// 		if(!isText(checkVal)) {
// 			return false;
// 		}
// 		checkVal = checkVal.toUpperCase();

// 		// is NONE
// 		if(checkVal == 'NONE' && !!!noneOk) {
// 			return false;
// 		}

// 		// is ALL
// 		if(checkVal == 'ALL' && !!!allOk) {
// 			return false;
// 		}

// 		// pass
// 		return true;

// 	}

// 	function isRX(rx, noneOk, allOk) {
// 		return isEntity(rx, noneOk, allOk);
// 	}

// 	function isCE(ce, noneOk, allOk) {
// 		return isEntity(ce, noneOk, allOk);
// 	}

// 	function isTPA(ce, noneOk, allOk) {
// 		return isEntity(ce, noneOk, allOk);
// 	}

// 	function isVEN(ven, noneOk, allOk) {
// 		return isEntity(ven, noneOk, allOk);
// 	}

// 	/**
// 	 *    WINDOW SUPPORT
// 	 */

function blobSupported(): boolean {
    // both pdf and Excel file generation depends on the browser supporting window.Blob feature

    if (!!window.Blob) {
        try {
            var testBlob = new window.Blob(['<p>test</p>'], { type: 'text/html' });
            if (!!testBlob) {
                return true;
            }
        } catch (err) {
            // do nothing
        }
    }

    return false;
}

// 	function uploadSupported() {

// 		// file upload depends on browser supporting specific window libraries

// 		if(typeof(window.File) === 'undefined' ||
// 			typeof(window.FileReader) === 'undefined' ||
// 			typeof(window.FileList) === 'undefined' ||
// 			typeof(window.Blob) === 'undefined' ||
// 			typeof(window.FormData) === 'undefined') {
// 				return false;
// 		}

// 		return true;
// 	}

// }

export default validSvc;
